import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Grid, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SectionHeader from '../../../components/shared/SectionHeader';
import CurrencyField from '../../../components/form/CurrencyField';
import TextField from '../../../components/form/TextField';

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

const AssetsStep = () => {
  const { setValue } = useFormContext();
  const [checking, savings, retirement, other, giftFunds] = useWatch({
    name: [
      'assets.checkingAccounts',
      'assets.savingsAccounts',
      'assets.retirementAccounts',
      'assets.otherAssets',
      'assets.giftFunds',
    ],
  });

  const calculated =
    num(checking) + num(savings) + num(retirement) + num(other) + num(giftFunds);

  useEffect(() => {
    if (calculated > 0) {
      setValue('assets.totalAssets', calculated, { shouldValidate: false, shouldDirty: false });
    }
  }, [calculated, setValue]);

  return (
    <Box>
      <SectionHeader title="Assets" icon={<AccountBalanceIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        List your financial assets including bank accounts, investments, and other holdings
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="assets.checkingAccounts" label="Checking Accounts" size="small" helperText="Total balance across all checking accounts" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="assets.savingsAccounts" label="Savings Accounts" size="small" helperText="Total balance across all savings accounts" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="assets.retirementAccounts" label="Retirement Accounts" size="small" helperText="401(k), IRA, Roth IRA, etc." />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrencyField name="assets.otherAssets" label="Other Assets" size="small" helperText="Stocks, bonds, mutual funds, etc." />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <SectionHeader title="Gift Funds" icon={<CardGiftcardIcon />} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <CurrencyField name="assets.giftFunds" label="Gift Amount" size="small" />
          </Grid>
          {num(giftFunds) > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="assets.giftSource"
                label="Gift Source"
                required
                size="small"
                placeholder="e.g., Parent, Relative, Employer"
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={{ mt: 3 }}>
        <CurrencyField
          name="assets.totalAssets"
          label="Total Assets"
          size="small"
          helperText="Auto-calculated from the fields above; you can override if needed"
        />
      </Box>
    </Box>
  );
};

export default AssetsStep;
